import { DatePipe, NgFor, NgIf } from '@angular/common';
import {
	Component,
	EventEmitter,
	Input,
	OnChanges,
	OnDestroy,
	Output
} from '@angular/core';
import { ChartConfiguration } from 'chart.js';
import { NgChartsModule } from 'ng2-charts';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { IconComponent, TypographyComponent } from 'components';
import { ChartConfigService } from '@brand-lolly/app/social-media-influencer/modules/discovery-v2/components/discovery-profile/services/chart-config.service';
import {
	AnalyticsEmotionResponse,
	GetSocialListeningMyBrandAnalyticsEmotionService
} from '@brand-lolly/app/social-media-influencer/modules/social-listening/services/get-social-listening-my-brand-analytics-emotion.service';
import {
	ChartWidget,
	PreviewSlide,
	ReportDateRange,
	SummaryTextWidget,
	TitleWidget
} from '../../../../models/preview/preview.models';
import { ReportContentItem } from '../../../../services/report-settings.service';
import {
	applyContentItemOverrides,
	hasPositiveDataPointTotal
} from '../../../../services/report-preview-utils';
import { SlideNoDataComponent } from '../slide-no-data/slide-no-data.component';

// The eight Plutchik emotions in the LOLLY report design order (Figma frame
// 13302:100519), each with its band colour. `dataKey` points at the backing
// field on AnalyticsEmotionResponse; Trust and Anticipation have no field in
// the analytics response, so they render as legend placeholders only.
const EMOTION_SERIES: {
	label: string;
	color: string;
	dataKey: keyof AnalyticsEmotionResponse | null;
}[] = [
	{ label: 'Joy', color: '#f5c451', dataKey: 'joyData' },
	{ label: 'Trust', color: '#6cd6c8', dataKey: null },
	{ label: 'Anticipation', color: '#a98cf0', dataKey: null },
	{ label: 'Surprise', color: '#f0a3c6', dataKey: 'surpriseData' },
	{ label: 'Sadness', color: '#5b8def', dataKey: 'sadnessData' },
	{ label: 'Anger', color: '#e94f6a', dataKey: 'angerData' },
	{ label: 'Fear', color: '#9aa3bb', dataKey: 'fearData' },
	{ label: 'Disgust', color: '#c4b8a0', dataKey: 'disgustData' }
];

// Series that actually have a backing analytics field (drives the stacked area).
const PLOTTED_SERIES = EMOTION_SERIES.filter(series => series.dataKey);

interface LegendItem {
	id: string;
	label: string;
	color: string;
}

interface KpiCallout {
	value: string;
	label: string;
	color: string;
}

import { prepareForDrawing } from '../../../../services/report-preview-chart-configs';
import { ReportInlineTextComponent } from '../../components/report-inline-text/report-inline-text.component';
import {
	NO_SLIDE_TEXT_FIELDS,
	ReportSlideTextFields,
	slideTextFields
} from '../../../../services/report-inline-edit.service';
import { ReportBlockRender } from '../../../../services/report-blocks.service';
import {
	ReportSectionChartSwitch,
	sectionChartSource,
	sectionChartSwitch
} from '../../../../services/report-chart-types';
import { ReportChartSwitchComponent } from '../../components/report-chart-switch/report-chart-switch.component';
import { ReportSlideBrandComponent } from '../../components/report-slide-brand/report-slide-brand.component';
@Component({
	standalone: true,
	selector: 'app-emotion-over-time-slide',
	templateUrl: './emotion-over-time-slide.component.html',
	styleUrls: ['./emotion-over-time-slide.component.scss'],
	imports: [
		NgIf,
		NgFor,
		IconComponent,
		TypographyComponent,
		NgChartsModule,
		SlideNoDataComponent,
		ReportInlineTextComponent,
		ReportChartSwitchComponent,
		ReportSlideBrandComponent
	],
	providers: [DatePipe]
})
export class EmotionOverTimeSlideComponent implements OnChanges, OnDestroy {
	@Input() item!: ReportContentItem;
	@Input() brandId!: string;
	@Input() dateRange!: ReportDateRange;
	@Input() themeColor: string | null = null;
	@Input() editorMode = false;
	@Output() slideBuilt = new EventEmitter<PreviewSlide>();
	/** The reader picked another type for this section's chart. */
	@Output() chartTypeChange = new EventEmitter<ReportBlockRender | null>();

	/** The heading and summary the reader can type into, in the editor. */
	textFields: ReportSlideTextFields = NO_SLIDE_TEXT_FIELDS;

	title = '';
	titleColor = '#2872e1';
	dateLabel = '';
	summaryText = '';
	chartData: ChartConfiguration<'line'>['data'] | null = null;
	chartOptions: ChartConfiguration<'line'>['options'] | null = null;
	// Follows the data: a period too short for a line is drawn as bars.
	chartType: 'line' | 'bar' = 'line';
	// Names the unit, so it follows the chart: shares as designed, counts
	// once it is redrawn as anything else (see updateChartSwitch).
	chartTitle = 'Emotion mix (% of daily mentions)';
	legendItems: LegendItem[] = [];
	primaryKpi: KpiCallout | null = null;
	/** What the chart is drawn as: its own design until the reader switches. */
	chartSwitch: ReportSectionChartSwitch = sectionChartSwitch(
		'emotion_over_time',
		null,
		null
	);

	private readonly destroy$ = new Subject<void>();
	private cachedEmotionData: AnalyticsEmotionResponse | null = null;
	private cacheKey: string | null = null;

	get isEmpty(): boolean {
		return (
			!this.cachedEmotionData ||
			!PLOTTED_SERIES.some(series =>
				hasPositiveDataPointTotal(
					this.cachedEmotionData![series.dataKey!]
				)
			)
		);
	}

	constructor(
		private readonly emotionService: GetSocialListeningMyBrandAnalyticsEmotionService,
		private readonly chartConfigService: ChartConfigService,
		private readonly datePipe: DatePipe
	) {}

	ngOnChanges(): void {
		this.destroy$.next();
		if (!this.item || !this.brandId || !this.dateRange) return;

		const currentCacheKey = `${this.brandId}:${this.dateRange.start}:${this.dateRange.end}`;
		if (this.cachedEmotionData && this.cacheKey === currentCacheKey) {
			this.applyEmotionData(this.cachedEmotionData);
			return;
		}

		this.emotionService
			// Scope the series to the report's selected period (from/to) —
			// without it the endpoint defaults to all-time.
			.fetch(
				this.brandId,
				new Date(this.dateRange.start),
				undefined,
				new Date(this.dateRange.end)
			)
			.pipe(takeUntil(this.destroy$))
			.subscribe(data => {
				this.cachedEmotionData = data;
				this.cacheKey = currentCacheKey;
				this.applyEmotionData(data);
			});
	}

	private applyEmotionData(data: AnalyticsEmotionResponse): void {
		const rawSlide = this.buildSlide(data);
		const slide = applyContentItemOverrides(
			rawSlide,
			this.item,
			this.themeColor
		);
		this.updateViewModel(slide);
		this.updateChartSwitch(data);
		this.slideBuilt.emit(slide);
	}

	/**
	 * The emotions as COUNTS, for any other type.
	 *
	 * The design draws each day as shares of 100%, and those shares are a
	 * drawing of the counts rather than numbers of their own: a pie of them,
	 * or a card with their "total", would add up percentages of different
	 * days. So a redrawn chart gets the counts the shares were made from, in
	 * the colours the legend above names.
	 */
	private updateChartSwitch(data: AnalyticsEmotionResponse): void {
		const colorByLabel = new Map(
			this.legendItems.map(item => [item.label, item.color])
		);
		const timeline =
			PLOTTED_SERIES.map(series => data[series.dataKey!] ?? []).find(
				points => points.length
			) ?? [];
		this.chartSwitch = sectionChartSwitch(
			this.item.id,
			this.item.chartType,
			this.chartData
				? sectionChartSource(this.item.id, {
						labels: (this.chartData.labels ?? []) as string[],
						series: PLOTTED_SERIES.map(series => ({
							key: String(series.dataKey),
							label: series.label,
							values: timeline.map(
								(_, index) =>
									data[series.dataKey!]?.[index]?.value ??
									null
							),
							color:
								colorByLabel.get(series.label) || series.color
						})),
						seriesLegendShown: true
				  })
				: null
		);
		this.chartTitle = this.chartSwitch.block
			? 'Emotion mentions'
			: 'Emotion mix (% of daily mentions)';
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	private buildSlide(data: AnalyticsEmotionResponse): PreviewSlide {
		const prefix = `${this.item.id}_${this.brandId}`;

		// Raw per-day values for each plotted series (aligned by index).
		const rawSeries = PLOTTED_SERIES.map(series =>
			(data[series.dataKey!] ?? []).map(point => point.value)
		);
		const timeline =
			PLOTTED_SERIES.map(series => data[series.dataKey!] ?? []).find(
				points => points.length
			) ?? [];
		const labels = timeline.map(
			point => this.datePipe.transform(point.timestamp, 'dd MMM') ?? ''
		);

		// 100%-stacked area: normalise each day so the emotions sum to 100%.
		const normalisedSeries = rawSeries.map(values =>
			values.map((value, index) => {
				const dayTotal = rawSeries.reduce(
					(sum, other) => sum + (other[index] ?? 0),
					0
				);
				return dayTotal > 0 ? (value / dayTotal) * 100 : 0;
			})
		);

		const datasets = PLOTTED_SERIES.map((series, index) => ({
			label: series.label,
			data: normalisedSeries[index],
			borderColor: series.color,
			backgroundColor: series.color,
			pointBackgroundColor: series.color,
			pointBorderColor: 'transparent' as const,
			// First band fills to the axis; the rest fill to the band below,
			// producing clean stacked ribbons.
			fill: index === 0 ? ('origin' as const) : ('-1' as const),
			borderWidth: 1,
			pointRadius: 0,
			pointHoverRadius: 3,
			tension: 0.35,
			type: 'line' as const
		}));

		const chartJsConfig: ChartConfiguration<'line'> = {
			type: 'line',
			data: { labels, datasets },
			options: {
				responsive: true,
				maintainAspectRatio: false,
				interaction: { mode: 'index', intersect: false },
				plugins: {
					legend: { display: false },
					tooltip: this.chartConfigService.getTooltipConfig() as any
				},
				scales: {
					x: {
						stacked: true,
						offset: false,
						border: { display: false },
						grid: { display: false },
						ticks: {
							color: '#6b7796',
							font: { family: 'Gilroy', size: 11 },
							maxRotation: 0,
							autoSkip: true,
							autoSkipPadding: 16
						}
					},
					y: {
						stacked: true,
						min: 0,
						max: 100,
						border: { display: false },
						grid: { color: '#e6eaf2', drawTicks: false },
						ticks: {
							color: '#6b7796',
							font: { family: 'Gilroy', size: 11 },
							padding: 8,
							stepSize: 25,
							callback: (value: string | number) => `${value}%`
						}
					}
				} as any
			}
		};

		// KPI callout: Joy's overall share of all plotted emotion volume.
		const joyRaw = rawSeries[0] ?? [];
		const joyTotal = joyRaw.reduce((sum, value) => sum + value, 0);
		const grandTotal = rawSeries.reduce(
			(sum, values) =>
				sum + values.reduce((inner, value) => inner + value, 0),
			0
		);
		const joyPercent =
			grandTotal > 0 ? (joyTotal / grandTotal) * 100 : undefined;
		const joyShare =
			joyPercent !== undefined ? `${Math.round(joyPercent)}%` : '—';

		const chartWidget: ChartWidget = {
			id: `${prefix}_chart`,
			kind: 'chart',
			visible: true,
			content: {
				chartType: 'line',
				dataSourceKey: 'emotion_over_time',
				seriesMeta: EMOTION_SERIES.map(series => ({
					key: series.dataKey ?? series.label.toLowerCase(),
					label: series.label,
					color: series.color
				})),
				chartJsConfig: chartJsConfig as unknown as Record<
					string,
					unknown
				>
			}
		};

		return {
			id: `${prefix}_slide`,
			slideType: 'emotion_over_time',
			title: this.item.title || 'Emotion Over Time',
			description: this.item.description || '',
			dateRange: this.dateRange,
			widgets: [
				{
					id: `${prefix}_title`,
					kind: 'title',
					visible: true,
					content: {
						text: this.item.title || 'Emotion Over Time',
						color: '#2872e1'
					}
				} as TitleWidget,
				{
					id: `${prefix}_date`,
					kind: 'date_range',
					visible: true,
					content: { text: this.dateRange.label }
				} as TitleWidget,
				{
					id: `${prefix}_summary`,
					kind: 'summary_text',
					visible: true,
					content: {
						text:
							this.item.description ||
							'Emotional trends across all monitored channels over time.'
					}
				} as SummaryTextWidget,
				chartWidget,
				{
					id: `${prefix}_kpi_joy_share`,
					kind: 'kpi',
					visible: true,
					content: {
						label: 'Joy share',
						value: joyShare,
						amount: joyPercent,
						unit: '%',
						legendColor: '#2872e1'
					}
				}
			]
		};
	}

	private updateViewModel(slide: PreviewSlide): void {
		this.textFields = slideTextFields(slide);
		const hidden = new Set(slide.overrides?.hiddenWidgetIds ?? []);

		const titleWidget = slide.widgets.find(
			widget => widget.kind === 'title' && !hidden.has(widget.id)
		) as TitleWidget | undefined;
		const dateWidget = slide.widgets.find(
			widget => widget.kind === 'date_range' && !hidden.has(widget.id)
		) as TitleWidget | undefined;
		const summaryWidget = slide.widgets.find(
			widget => widget.kind === 'summary_text' && !hidden.has(widget.id)
		) as SummaryTextWidget | undefined;

		this.title = titleWidget?.content.text ?? slide.title;
		this.titleColor = titleWidget?.content.color ?? '#2872e1';
		this.dateLabel =
			dateWidget?.content.text ?? slide.dateRange?.label ?? '';
		this.summaryText = summaryWidget?.content.text ?? '';

		const chartWidget = (slide.widgets.find(
			widget => widget.kind === 'chart' && !hidden.has(widget.id)
		) ?? null) as ChartWidget | null;

		if (chartWidget) {
			const config = chartWidget.content
				.chartJsConfig as unknown as ChartConfiguration<'line'>;
			this.chartData = config?.data ?? null;
			if (config) {
				this.chartType = prepareForDrawing(
					config as unknown as ChartConfiguration
				) as 'line' | 'bar';
			}
			this.chartOptions = config?.options ?? null;
			this.legendItems = (chartWidget.content.seriesMeta ?? []).map(
				meta => ({
					id: meta.key,
					label: meta.label,
					color: meta.color ?? ''
				})
			);
		} else {
			this.chartData = null;
			this.chartOptions = null;
			this.legendItems = [];
		}

		const kpiWidget = slide.widgets.find(
			widget => widget.kind === 'kpi' && !hidden.has(widget.id)
		) as any;
		this.primaryKpi = kpiWidget
			? {
					value: kpiWidget.content.value,
					label: kpiWidget.content.label,
					color: kpiWidget.content.legendColor ?? '#2872e1'
			  }
			: null;
	}

	trackByLegendId(_index: number, item: LegendItem): string {
		return item.id;
	}
}
